import { FieldTitle } from './RedStarTitle';
import Select from 'react-select';
import { Controller, useFormContext } from 'react-hook-form';
import { FieldDesc } from './common';
import { TextField } from '@client/new/components/basic';
import { SubOfferData } from './types';
import { TogglableButtons } from '@design-system/components/togglableButtons';
import { isEmpty } from 'lodash';

type Props = {
  index: number;
  offer: SubOfferData | null;
};

export function SubscriptionCarousel({ index, offer }: Props) {
  const { register, control } = useFormContext();
  const r = (s: string) => register(`sections.${index}.${s}`);

  if (!offer) return 'Offer data is missing.';

  return (
    <div className="flex flex-col md:flex-row gap-6">
      <div className="flex flex-col gap-6 w-[50%]">
        <div>
          <FieldTitle title="Headline text" />
          <FieldDesc textClassName="font-normal text-sm">
            The headline is used to communicate the creator’s value proposition
            in the hero section of the sales page.
          </FieldDesc>
          <TextField
            {...r('title')}
            className="mb-[5px] w-full"
            inputProps={{
              placeholder: 'Headline text',
              maxLength: 60,
            }}
          />
        </div>

        <div>
          <FieldTitle title="Subtitle" optional />
          <TextField
            {...r('subtitle')}
            className="mb-[5px] w-full"
            inputProps={{
              placeholder: 'Subtitle text',
              maxLength: 120,
            }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-6">
        {!isEmpty(offer.programs) && (
          <div>
            <FieldTitle title="Choose Programs" optional />
            <FieldDesc textClassName="font-normal text-sm">
              Will be displayed in selected order. By default, the first 5
              programs are displayed.
            </FieldDesc>
            <Controller
              name={`sections.${index}.programIds`}
              control={control}
              render={({
                field: { value, onChange, ref },
                fieldState: { error },
              }) => (
                <>
                  <Select
                    ref={ref}
                    isMulti
                    options={offer.programs}
                    value={(value || [])
                      .map((id: number) =>
                        (offer.programs || []).find((p) => p.value === id),
                      )
                      .filter(Boolean)}
                    onChange={(options) =>
                      onChange(options.map((o) => o.value))
                    }
                    placeholder="Select programs..."
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: '56px',
                        borderColor: error ? '#d32f2f' : provided.borderColor,
                      }),
                    }}
                  />
                  {error && (
                    <div className="text-red-600 text-sm mt-1">
                      {error.message}
                    </div>
                  )}
                </>
              )}
            />
          </div>
        )}
        {!isEmpty(offer.content) && (
          <div>
            <FieldTitle title="Choose Extras" optional />
            <FieldDesc textClassName="font-normal text-sm">
              Will be displayed in selected order. By default, the first 5 items
              are displayed.
            </FieldDesc>
            <Controller
              name={`sections.${index}.extrasIds`}
              control={control}
              render={({
                field: { value, onChange, ref },
                fieldState: { error },
              }) => (
                <>
                  <Select
                    ref={ref}
                    isMulti
                    options={offer.content}
                    value={(offer.content || []).filter((o) =>
                      (value || []).includes(o.value),
                    )}
                    onChange={(options) =>
                      onChange(options.map((o) => o.value))
                    }
                    placeholder="Select extras..."
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: '56px',
                        borderColor: error ? '#d32f2f' : provided.borderColor,
                      }),
                    }}
                  />
                  {error && (
                    <div className="text-red-600 text-sm mt-1">
                      {error.message}
                    </div>
                  )}
                </>
              )}
            />
          </div>
        )}
        {!isEmpty(offer.features) && (
          <div>
            <FieldTitle title={'Community Features'} optional />
            <FieldDesc textClassName="font-normal text-sm">
              Select the community features included in this offer.
            </FieldDesc>
            <Controller
              name={`sections.${index}.communityFeatures`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <TogglableButtons
                  options={(offer.features || []).map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  value={value || []}
                  onChange={onChange}
                />
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}
